import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyOceancleanupMode({ brand, today, Copy }: ModeRendererProps) {
  // Create stable, namespaced IDs to avoid collisions between modes
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `oceancleanup_${param}` : `oceancleanup_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `oceancleanup_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);
  const gradId = `${idBase}_grad`;
  const arrowId = `${idBase}_arrow`;

  // Consolidated defs (single source of truth)
  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#006994" />
        <stop offset="50%" stopColor="#00BCD4" />
        <stop offset="100%" stopColor="#FF6B35" />
      </linearGradient>
      <marker id={arrowId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
        <polygon points="0 0, 8 4, 0 8" fill="#00BCD4" />
      </marker>
    </defs>
  );

  // Gyre spirals: ocean plastic accumulation
  const gyreSpirals: JSX.Element[] = [];
  for (let i = 0; i < 6; i++) {
    const points: string[] = [];
    for (let j = 0; j < 48; j++) {
      const angle = (j * Math.PI * 2) / 48;
      const radius = 50 + j * 4 + i * 20;
      points.push(`${397 + radius * Math.cos(angle + i)},${561.5 + radius * Math.sin(angle + i)}`);
    }
    gyreSpirals.push(
      <polyline
        key={`gyre-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={2}
        opacity={0.4}
      />
    );
  }

  // Vector fields: ocean current direction
  const vectorFields: JSX.Element[] = [];
  for (let row = 0; row < 14; row++) {
    for (let col = 0; col < 10; col++) {
      const x = 100 + col * 60;
      const y = 120 + row * 65;
      const flowAngle = Math.sin(row * 0.3) + Math.cos(col * 0.4);
      const dx = Math.cos(flowAngle) * 20;
      const dy = Math.sin(flowAngle) * 20;
      vectorFields.push(
        <line
          key={`vector-${row}-${col}`}
          x1={x}
          y1={y}
          x2={x + dx}
          y2={y + dy}
          stroke={`url(#${gradId})`}
          strokeWidth={1.5}
          opacity={0.4}
          markerEnd={`url(#${arrowId})`}
        />
      );
    }
  }

  // Marine lattices: cleanup system grid
  const marineLattices: JSX.Element[] = [];
  for (let i = 0; i < 16; i++) {
    const y = 100 + i * 58;
    marineLattices.push(
      <line
        key={`lat-h-${i}`}
        x1={70}
        y1={y}
        x2={724}
        y2={y}
        stroke="#00BCD4"
        strokeWidth={1}
        opacity={0.3}
        strokeDasharray="4,8"
      />
    );
  }

  // Plastic drift particles
  const plasticDrift: JSX.Element[] = [];
  // Deterministic pseudo-random distribution for stable renders
  for (let i = 0; i < 80; i++) {
    const angle = (i * 2.399963229728653) % (Math.PI * 2); // golden-angle-like spread
    const radius = 100 + ((i * 37) % 200);
    const x = 397 + radius * Math.cos(angle + (i % 5) * 0.1);
    const y = 561.5 + radius * Math.sin(angle + (i % 7) * 0.07);
    const size = 2 + ((i * 17) % 4);
    plasticDrift.push(
      <circle key={`plastic-${i}`} cx={x} cy={y} r={size} fill="#FF6B35" opacity={0.5} />
    );
  }

  // Central cleanup system
  const cleanupCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={100} fill="none" stroke="#00BCD4" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={70} fill="none" stroke="#006994" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={40} fill="none" stroke="#FF6B35" strokeWidth={2} />
      {/* Cleanup barrier */}
      <path
        d="M 347 561.5 Q 397 531.5 447 561.5 Q 397 591.5 347 561.5"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={3}
      />
    </g>
  );

  return (
    <g>
      {defs}
      {marineLattices}
      {vectorFields}
      {gyreSpirals}
      {plasticDrift}
      {cleanupCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}
